import { computed, type Ref } from "vue";
import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import type { ApiError } from "@/helpers/api-error";
import type { IOrg, IUser } from "@/types";

const BASE = `${import.meta.env.VITE_API_URL}/orgs`;

export interface ValidateInviteResponse {
    orgId: string;
    orgName: string;
    email: string;
    role: string;
    isNewUser: boolean;
    authType: string;
}

interface ActivateInvitePayload {
    token: string;
    password?: string;
}

interface ActivateInviteResponse {
    redirectUrl: string;
}

interface DeclineInvitePayload {
    token: string;
}

// READ — current org
export function useCurrentOrg() {
    const { data: org, isFetching: orgLoading, error: orgError, refetch: refetchOrg } = useQuery({
        queryKey: ["current-org"],
        queryFn: () => fetchWrapper.get<IOrg>(`${BASE}/current-org`),
    });

    return { org, orgLoading, orgError: orgError as Ref<ApiError | null>, refetchOrg };
}

// READ — public invite details
export function useValidateInvite(token: Ref<string | undefined>) {
    const { data, isFetching, error, refetch } = useQuery<ValidateInviteResponse, ApiError>({
        queryKey: ["org-invite", "validate", token],
        enabled: computed(() => !!token.value),
        retry: false,
        queryFn: () => {
            if (!token.value) throw new Error("Invite token is required.");
            return fetchWrapper.get<ValidateInviteResponse>(
                `${BASE}/invite/validate?token=${encodeURIComponent(token.value)}`,
                undefined,
                { skipAuthRedirect: true });
        },
    });

    return {
        inviteDetails: data,
        inviteDetailsLoading: isFetching,
        inviteDetailsError: error as Ref<ApiError | null>,
        refetchInviteDetails: refetch,
    };
}

// WRITE — public invite activation
export function useActivateInvite() {
    const { mutate, mutateAsync, isPending, error, reset } = useMutation<ActivateInviteResponse, ApiError, ActivateInvitePayload>({
        mutationFn: (payload: ActivateInvitePayload) =>
            fetchWrapper.post(`${BASE}/invite/activate`, payload, { skipAuthRedirect: true }),
    });

    return {
        activateInvite: mutate,
        activateInviteAsync: mutateAsync,
        activateInvitePending: isPending,
        activateInviteError: error as Ref<ApiError | null>,
        activateInviteReset: reset,
    };
}

// WRITE — public invite decline
export function useDeclineInvite() {
    const { mutate, mutateAsync, isPending, error, reset } = useMutation<void, ApiError, DeclineInvitePayload>({
        mutationFn: (payload: DeclineInvitePayload) =>
            fetchWrapper.post(`${BASE}/invite/decline`, payload, { skipAuthRedirect: true }),
    });

    return {
        declineInvite: mutate,
        declineInviteAsync: mutateAsync,
        declineInvitePending: isPending,
        declineInviteError: error as Ref<ApiError | null>,
        declineInviteReset: reset,
    };
}

// READ — users in current org
export function useOrgUsers() {
    const { data, isFetching: orgUsersLoading, error: orgUsersError, refetch: refetchOrgUsers } = useQuery({
        queryKey: ["org-users"],
        queryFn: () => fetchWrapper.get<IUser[]>(`${BASE}/users`),
    });
    const orgUsers = computed(() => data.value ?? []);

    return { orgUsers, orgUsersLoading, orgUsersError: orgUsersError as Ref<ApiError | null>, refetchOrgUsers };
}

// WRITE — switch org
export function useSwitchOrganisation() {
    const { mutate, mutateAsync, isPending, error, reset } = useMutation<void, ApiError, string>({
        mutationFn: (orgId: string) => fetchWrapper.post(`${BASE}/switch-org/${orgId}`),
    });

    return {
        switchOrganisation: mutate,
        switchOrganisationAsync: mutateAsync,
        switchOrganisationPending: isPending,
        switchOrganisationError: error as Ref<ApiError | null>,
        switchOrganisationReset: reset,
    };
}

// WRITE — update current org
interface UpdateOrgPayload {
    orgName?: string;
    primaryContactUserId?: string;
}

export function useUpdateOrg() {
    const queryClient = useQueryClient();

    const { mutate, mutateAsync, isPending, error, reset } = useMutation<void, ApiError, UpdateOrgPayload>({
        mutationFn: (payload: UpdateOrgPayload) => fetchWrapper.put(`${BASE}/`, payload),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["current-org"] }),
    });

    return {
        updateOrg: mutate,
        updateOrgAsync: mutateAsync,
        updateOrgPending: isPending,
        updateOrgError: error as Ref<ApiError | null>,
        updateOrgReset: reset,
    };
}

// WRITE — create org
export function useCreateOrg() {
    const queryClient = useQueryClient();

    const { mutate, mutateAsync, isPending, error, reset } = useMutation<void, ApiError, string>({
        mutationFn: (orgName: string) => fetchWrapper.post(`${BASE}/`, { orgName }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["current-org"] }),
    });

    return {
        createOrg: mutate,
        createOrgAsync: mutateAsync,
        createOrgPending: isPending,
        createOrgError: error as Ref<ApiError | null>,
        createOrgReset: reset,
    };
}

// WRITE — admin: add user to org
interface AddUserToOrgPayload {
    orgId: string;
    userId: string;
    role: string;
    firstName?: string;
    lastName?: string;
}

export function useAdminAddUserToOrg() {
    const queryClient = useQueryClient();

    const { mutate, mutateAsync, isPending, error, reset } = useMutation<void, ApiError, AddUserToOrgPayload>({
        mutationFn: ({ orgId, userId, role, firstName, lastName }: AddUserToOrgPayload) =>
            fetchWrapper.post(`${BASE}/${orgId}/users`, {
                userId,
                role,
                firstName: firstName || undefined,
                lastName: lastName || undefined,
            }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["org-users"] }),
    });

    return {
        adminAddUserToOrg: mutate,
        adminAddUserToOrgAsync: mutateAsync,
        adminAddUserToOrgPending: isPending,
        adminAddUserToOrgError: error as Ref<ApiError | null>,
        adminAddUserToOrgReset: reset,
    };
}

// WRITE — admin: update user role
interface UpdateUserRolePayload {
    orgId: string;
    userId: string;
    role: string;
}

export function useAdminUpdateUserRole() {
    const queryClient = useQueryClient();

    const { mutate, mutateAsync, isPending, error, reset } = useMutation<void, ApiError, UpdateUserRolePayload>({
        mutationFn: ({ orgId, userId, role }: UpdateUserRolePayload) =>
            fetchWrapper.put(`${BASE}/${orgId}/users/${userId}`, { role }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["org-users"] }),
    });

    return {
        adminUpdateUserRole: mutate,
        adminUpdateUserRoleAsync: mutateAsync,
        adminUpdateUserRolePending: isPending,
        adminUpdateUserRoleError: error as Ref<ApiError | null>,
        adminUpdateUserRoleReset: reset,
    };
}

// WRITE — resend pending invitation
export function useResendInvite() {
    const queryClient = useQueryClient();

    const { mutate, isPending, error, reset } = useMutation<void, ApiError, string>({
        mutationFn: (email: string) => fetchWrapper.post(`${BASE}/invite/resend`, { email }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["org-users"] }),
    });

    return {
        resendInvite: mutate,
        resendInvitePending: isPending,
        resendInviteError: error as Ref<ApiError | null>,
        resendInviteReset: reset,
    };
}

// WRITE — revoke pending invitation
export function useRevokeInvite() {
    const queryClient = useQueryClient();

    const { mutate, isPending, error, reset } = useMutation<void, ApiError, string>({
        mutationFn: (email: string) => fetchWrapper.delete(`${BASE}/invite?email=${encodeURIComponent(email)}`),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["org-users"] }),
    });

    return {
        revokeInvite: mutate,
        revokeInvitePending: isPending,
        revokeInviteError: error as Ref<ApiError | null>,
        revokeInviteReset: reset,
    };
}

// WRITE — admin: remove user from org
interface RemoveUserPayload {
    orgId: string;
    userId: string;
}

export function useAdminRemoveUserFromOrg() {
    const queryClient = useQueryClient();

    const { mutate, mutateAsync, isPending, error, reset } = useMutation<void, ApiError, RemoveUserPayload>({
        mutationFn: ({ orgId, userId }: RemoveUserPayload) =>
            fetchWrapper.delete(`${BASE}/${orgId}/users/${userId}`),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["org-users"] }),
    });

    return {
        adminRemoveUserFromOrg: mutate,
        adminRemoveUserFromOrgAsync: mutateAsync,
        adminRemoveUserFromOrgPending: isPending,
        adminRemoveUserFromOrgError: error as Ref<ApiError | null>,
        adminRemoveUserFromOrgReset: reset,
    };
}
